import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { useTranslation } from "react-i18next";
import styled, { withTheme } from "styled-components";
import { decimalRegex, FoodType } from "../../../../modules/constants";
import DefaultTable, {
  Line,
} from "../../../ui-library/CustomTable/DefaultTable";
import OutlineAccentExpandTitleButton from "../../../ui-library/Buttons/OutlineAccentExpandTitleButton/OutlineAccentExpandTitleButton";
import PrimaryExpandTitleButton from "../../../ui-library/Buttons/PrimaryButton/PrimaryExpandTitleButton";
import { TableDropDownMenu } from "../../../ui-library/TableDropDown/TableDropDown";

const StyledDiv = styled("div")`
  color: ${(props) => props?.theme?.black};

  width: 100%;
  margin: auto;

  .dropdown-menu {
    left: -2em !important;
  }

  .hide {
    display: none;
  }

  h6 {
    font-weight: 600;
  }

  th {
    font-weight: 600;
  }

  th:first-child,
  td:first-child {
    width: 66%;
  }

  td {
    padding-top: 0.25em;
    padding-bottom: 0.25em;
  }

  .meal-food-search-wrapper {
    width: 100%;

    #food-drink-search {
      transform: translate3d(30px, 38px, 0px) !important;
    }
  }

  .meal-food-search-wrapper .form-control {
    border-bottom: 2px solid ${(props) => props?.theme?.pastelLike};

    &:hover,
    &:active,
    &:focus {
      border-bottom: 2px solid #a7208c;
    }
  }

  .meal-food-search-wrapper .form-control input {
    font-size: 15px;
    font-weight: 600;
  }

  .rbt-token-removeable {
    display: none;
  }
`;

const ItemColumn = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  visibility: ${(props) => (props?.removed ? "hidden" : "visible")};
`;

const StyledRemoveIcon = styled("i")`
  color: ${(props) => props?.theme?.mortar};
  font-size: 18px;
  margin-right: 9px;
  cursor: pointer;
`;

const StyledNoEntries = styled("div")`
  text-align: center;
  color: rgba(0, 0, 0, 0.22);
`;

const StyledInputQty = styled("input")`
  border-bottom: 2px solid ${(props) => props?.theme?.pastelLike};
  text-align: center;
  min-width: 45px;
  visibility: ${(props) => (props?.visible ? "visible" : "hidden")};

  &.form-control {
    font-weight: 600;
    font-size: 12px;
  }
  &.new-answer {
    color: ${(props) => props?.theme?.mortar};
    background-color: transparent;

    &:hover,
    &:active,
    &:focus {
      background-color: transparent;
    }
  }
`;

const StyledPrimaryExpandTitleButton = styled(PrimaryExpandTitleButton)`
  overflow: hidden;
`;

const StyledDropDwonToggle = styled(Dropdown.Toggle)`
  background-color: ${(props) => props?.theme?.whiteSmoke};
  border-color: ${(props) => props?.theme?.whiteSmoke};
  color: ${(props) => props?.theme?.mortar};
  padding: 0.5rem 0.7rem 0.5rem 0.4rem;
  font-weight: 600;
  font-size: 0.75rem;
  width: 65px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &:active,
  &:focus,
  &.focus {
    background-color: ${(props) => props?.theme?.whiteSmoke};
    border-color: ${(props) => props?.theme?.whiteSmoke};
    color: ${(props) => props?.theme?.mortar};
    box-shadow: 0 0.2rem 1rem rgba(0, 0, 0, 0.2);
  }

  &::after {
    font-family: "Font Awesome 5 Free";
    font-weight: 900;
    content: "\f107";
    border: 0px;
    vertical-align: initial;
    position: absolute;
    right: 5px;
  }
`;

const Title = styled.span`
  color: ${(props) => props.theme?.blackLite};
  font-weight: 600;
  font-size: 18px;
`;

const TitleDivider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme?.blackLite};
`;

const UnitDropdown = styled(Dropdown)`
  visibility: ${(props) => (props?.visible ? "visible" : "hidden")};
`;

const MCTIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  margin-right: -2px;
  margin-top: -2px;
  border-top-right-radius: 6.4px;
  border-bottom-right-radius: 6.4px;
  height: 53px;
  width: 7px;
  background-color: ${(props) => props.theme?.blueMagenta};
`;

const FoodDrinkamountDropDown = (props) => {
  const {
    selectedItems,
    setSelectedItems,
    selectedItem,
    lineNumber,
    placeInElementRef,
    readOnly,
    visible,
  } = props;

  const { t } = useTranslation();

  const [dropTitle, setDropTitle] = useState(null);

  useEffect(() => {
    setDropTitle(
      selectedItem?.foodType === FoodType.CUSTOM
        ? t(selectedItem.selectedMeasure.measure)
        : selectedItem.selectedMeasure.measure
    );
  }, [selectedItem, t]);

  const handleDropDownChange = (e, measure) => {
    setDropTitle(e.target.textContent);

    const newItem = { ...selectedItem, selectedMeasure: measure };

    setSelectedItems(
      selectedItems.map((item, index) => {
        return index === lineNumber ? newItem : item;
      })
    );
  };

  return (
    <UnitDropdown
      drop="down"
      data-testid="measures-dropdown"
      visible={visible ? "visible" : null}
    >
      <StyledDropDwonToggle variant="fodd-drink">
        {dropTitle}
      </StyledDropDwonToggle>
      <TableDropDownMenu placeInElement={placeInElementRef?.current}>
        <Dropdown.Item
          data-testid="default-measure-item"
          onSelect={(eventKey, e) => {
            if (!readOnly) {
              handleDropDownChange(e, selectedItem.selectedMeasure);
            }
          }}
        >
          {selectedItem.foodType === FoodType.CUSTOM
            ? t(selectedItem.selectedMeasure.measure)
            : selectedItem.selectedMeasure.measure}
        </Dropdown.Item>
        {!readOnly &&
          selectedItem?.measures?.map((item, i) => {
            return item.measure !== selectedItem.selectedMeasure.measure ? (
              <Dropdown.Item
                data-testid={`drop_item_${lineNumber}_${i}`}
                key={`drop_item_${lineNumber}_${i}`}
                onSelect={(eventKey, e) => {
                  handleDropDownChange(e, item);
                }}
              >
                {selectedItem.foodType === FoodType.CUSTOM
                  ? t(item.measure)
                  : item.measure}
              </Dropdown.Item>
            ) : null;
          })}
      </TableDropDownMenu>
    </UnitDropdown>
  );
};

const FoodDrinkQtyInput = (props) => {
  const {
    selectedItems,
    setSelectedItems,
    lineNumber,
    selectedItem,
    readOnly,
    visible,
  } = props;
  const [qty, setQty] = useState("");

  useEffect(() => {
    const value = selectedItem.qtyText ?? selectedItem.qty?.toString() ?? "";
    setQty(value);
  }, [selectedItem]);

  const handleQtyChange = (e) => {
    const newValue = e.target.value.replace(" ", "").replace(",", ".");

    if (newValue === "" || decimalRegex.test(newValue)) {
      setQty(newValue);

      const newValueParsed = parseFloat(newValue);

      const newItem = {
        ...selectedItem,
        qty: isNaN(newValueParsed) ? 0 : newValueParsed,
        qtyText: newValue,
      };

      setSelectedItems(
        selectedItems.map((item, index) => {
          return index === lineNumber ? newItem : item;
        })
      );
    }
  };

  return (
    <StyledInputQty
      visible={visible ? "visible" : null}
      data-testid="foodDrinkQtyInput"
      type="text"
      className="form-control new-answer"
      placeholder=""
      aria-label="Qty"
      aria-describedby="Qty"
      onChange={handleQtyChange}
      value={qty}
      readOnly={readOnly}
      maxLength="4"
    />
  );
};

const FoodDrinkSelector = (props) => {
  const {
    theme,
    inputTitle,
    inputPlaceholder,
    handleSearch,
    handleSelect,
    totalitems,
    selectedItems,
    setSelectedItems,
    children,
    defaultItems,
    readOnly,
    title = "My items",
    ...styledDivProps
  } = props;

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [options, setOptions] = useState([]);

  const [defaultItemAdded, setDefaultItemAdded] = useState(false);

  const handleFoodChange = (selectedOptions) => {
    setIsAdding(true);

    const tmpSelected = selectedOptions[selectedOptions.length - 1];

    onSelectItem(tmpSelected);
    setOptions([]);
  };

  const processSelectItem = useCallback((data) => {
    data.qty = 1;
    data.selectedMeasure = data?.defaultMeasure;
    return data;
  }, []);

  const onSelectItem = useCallback(
    (selected, asynchCall = true) => {
      if (asynchCall) {
        handleSelect(selected.item, selected.type, selected.locale)
          .then((data) => {
            data = processSelectItem(data);
            const tmpOptions = [data, ...selectedItems];
            setSelectedItems(tmpOptions);
            setIsAdding(false);
          })
          .finally(() => {
            setIsAdding(false);
          });
      } else {
        return handleSelect(selected.item, selected.type, selected.locale);
      }
    },
    [handleSelect, selectedItems, setSelectedItems, processSelectItem]
  );

  const searchData = (query) => {
    setIsLoading(true);

    handleSearch(query)
      .then((data) => {
        setOptions(data);
        setIsLoading(false);
      })
      .finally(() => {
        setIsAdding(false);
      });
  };

  useEffect(() => {
    if (defaultItems && !defaultItemAdded) {
      /*
       * When there is more than one default item we first build / obtain
       * the API information and then set the reducer information
       * in order to only make 1 reducer call instead of muliple ones.
       */
      const processAsyncDefaultItem = async () => {
        setDefaultItemAdded(true);

        let tmpData = [];
        for (let index = 0; index < defaultItems.length; index++) {
          const item = defaultItems[index];
          try {
            let data = await onSelectItem({
              title: item?.title,
              item: item?.item,
              type: item?.foodType,
            });
            data = processSelectItem(data);
            tmpData.push(data);
          } catch (error) {}
        }

        setSelectedItems(tmpData);
      };

      processAsyncDefaultItem();
    }
  }, [
    defaultItemAdded,
    defaultItems,
    onSelectItem,
    selectedItems,
    setSelectedItems,
    processSelectItem,
  ]);

  // Bypass client-side filtering by returning `true`. Results are already
  // filtered by the search endpoint, so no need to do it again.
  const filterBy = () => true;

  const defaultProps = {
    headers: [
      { label: t("appText.item") },
      { label: t("appText.qty") },
      { label: t("appText.unit") },
    ],
    maincolor: theme?.darkPurple,
    totalitems: totalitems,
    hasSearch: 0,
    haspagesizefilter: 0,
    haspagination: 0,
    searchedParams: {
      search: "",
    },
    noLineBorder: 1,
    topControlsClass: "hide",
    responsivetable: 0,
  };

  const placeInElementRef = useRef(null);

  return (
    <StyledDiv
      data-testid="foodDrinkSelector"
      className="food-drink-selector"
      ref={placeInElementRef}
      {...styledDivProps}
    >
      {readOnly ? (
        <>
          <Title>{title}</Title>
          <TitleDivider />
        </>
      ) : (
        <>
          <h6>{inputTitle}</h6>
          <div className="input-group mb-1">
            <AsyncTypeahead
              filterBy={filterBy}
              id="food-drink-search"
              labelKey="title"
              isLoading={isLoading || isAdding}
              minLength={1}
              onSearch={searchData}
              options={options}
              multiple
              autoFocus
              emptyLabel={t("appText.search.emptyLabel")}
              searchText={t("appText.search.searchText")}
              promptText={t("appText.search.promptText")}
              onInputChange={(text) => {
                if (text.length === 0) {
                  setOptions([]);
                }
              }}
              delay={500}
              useCache={false}
              placeholder={
                inputPlaceholder ||
                t("appText.dietDiary.addMeal.search.placeholder")
              }
              onChange={handleFoodChange}
              selected={selectedItems}
              renderMenuItemChildren={(option, props) => (
                <div>
                  <span>{option.title}</span>
                </div>
              )}
              className="meal-food-search-wrapper"
              inputStyle=""
              inputProps={{
                "data-testid": "inputSearchComponent",
                name: "inputSearchComponent",
              }}
            />
          </div>
        </>
      )}

      <div className="food-labels mb-3 mt-2">
        <DefaultTable {...{ ...defaultProps }}>
          {selectedItems?.map((selectedItem, index) => {
            return (
              <Line key={`table_line_${index}`}>
                <ItemColumn removed={selectedItem.removed}>
                  {!readOnly && (
                    <StyledRemoveIcon
                      onClick={() => {
                        const tmpMulti = [...selectedItems];
                        tmpMulti.splice(index, 1);
                        setSelectedItems([...tmpMulti]);
                      }}
                      className="fa fa-times"
                      aria-hidden="true"
                      data-testid="remove-button"
                    />
                  )}
                  {selectedItem.addedOrModified ? (
                    <OutlineAccentExpandTitleButton size="sm" cutAtChar="18">
                      {selectedItem.title?.toUpperCase()}
                    </OutlineAccentExpandTitleButton>
                  ) : (
                    <StyledPrimaryExpandTitleButton size="sm" cutAtChar="18">
                      {selectedItem.title?.toUpperCase()}

                      {selectedItem.foodType === FoodType.CUSTOM && (
                        <MCTIndicator />
                      )}
                    </StyledPrimaryExpandTitleButton>
                  )}
                </ItemColumn>
                <span>
                  <FoodDrinkQtyInput
                    visible={!selectedItem.removed}
                    quantity={selectedItem.qty}
                    selectedItem={selectedItem}
                    index={index}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    lineNumber={index}
                    readOnly={readOnly}
                  />
                </span>
                <span>
                  <FoodDrinkamountDropDown
                    visible={!selectedItem.removed}
                    selectedItem={selectedItem}
                    lineNumber={index}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    placeInElementRef={placeInElementRef}
                    readOnly={readOnly}
                  />
                </span>
              </Line>
            );
          })}
        </DefaultTable>
        {selectedItems?.length > 0 ? null : (
          <StyledNoEntries>
            {t("appText.dietDiary.addMeal.noEntries")}
          </StyledNoEntries>
        )}
      </div>

      {children}
    </StyledDiv>
  );
};

FoodDrinkSelector.propTypes = {
  inputTitle: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  totalitems: PropTypes.number,
  readOnly: PropTypes.bool,
};

FoodDrinkSelector.defaultProps = {
  inputTitle: "What did you eat?",
  totalitems: 0,
  readOnly: false,
};

export default withTheme(FoodDrinkSelector);
